if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.productCache = new Map();

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.addEventListener('product-info:loaded', ({target}) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
        this.elementSection = this.getAttribute('data-element');
        this.productTitle = this.getAttribute('data-product-title');
        this.productTitleVariant = this.getAttribute('data-label');
        this.buttonATC = this.modalContent.querySelector('.product-main-atc.product-form__submit');
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');
      
        const productUrl = opener.getAttribute('data-product-url');
        const fetchUrl = productUrl.includes('?') 
          ? `${productUrl}&view=basic` 
          : `${productUrl}?view=basic`;
        
        this.fetchAndRenderProduct(fetchUrl)
          .then(() => {
            super.show(opener);
            
            if (window.requestIdleCallback) {
              requestIdleCallback(() => this.prefetchColorVariants());
            } else {
              setTimeout(() => this.prefetchColorVariants(), 100);
            }
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Prefetch all color variant URLs
      prefetchColorVariants() {
        const colorLinks = this.modalContent.querySelectorAll('.product-variant-picker-color a[href]');
        
        colorLinks.forEach(link => {
          const baseUrl = link.getAttribute('href');
          if (!baseUrl) return;
          
          const url = baseUrl.includes('?') 
            ? `${baseUrl}&view=basic` 
            : `${baseUrl}?view=basic`;
          
          if (!this.productCache.has(url)) {
            fetch(url)
              .then(response => response.text())
              .then(html => {
                this.productCache.set(url, html);
              })
              .catch(error => {
                console.error(`Error prefetching ${url}:`, error);
              });
          }
        });
      }

      // Fetch and render product into modal
      fetchAndRenderProduct(productUrl) {
        // Check cache first
        if (this.productCache.has(productUrl)) {
          const cachedHTML = this.productCache.get(productUrl);
          this.renderProduct(cachedHTML);
          return Promise.resolve();
        }

        return fetch(productUrl)
          .then(response => response.text())
          .then(html => {
            // Cache the response
            this.productCache.set(productUrl, html);
            this.renderProduct(html);
          });
      }

      // Render product HTML into modal
      renderProduct(html) {
        const responseHTML = new DOMParser().parseFromString(html, 'text/html');
        const productElement = responseHTML.querySelector('product-info');

        this.preprocessHTML(productElement);
        this.removeScripts(productElement);

        HTMLUpdateUtility.setInnerHTML(
          this.modalContent,
          productElement.outerHTML,
        );

        // Init color variant click handler
        this.initColorVariantHandler();

        this.buttonATC = this.modalContent.querySelector('.product-main-atc.product-form__submit');
        if (this.buttonATC) {
          this.buttonATC.setAttribute('data-category', 'pdp-module');
          this.buttonATC.setAttribute('data-product-title', this.productTitle);
          this.buttonATC.setAttribute('data-label', this.productTitleVariant);
          this.buttonATC.setAttribute('data-element', this.elementSection);
        }

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
      }

      // Handle color variant clicks inside modal
      initColorVariantHandler() {
        const colorPicker = this.modalContent.querySelector('.product-variant-picker-color');
        if (!colorPicker) return;

        colorPicker.addEventListener('click', (event) => {
          const anchor = event.target.closest('a[href]');
          if (!anchor) return;
          
          event.preventDefault();
          event.stopPropagation();

          const baseUrl = anchor.getAttribute('href');
          if (!baseUrl) return;
          
          const newProductUrl = baseUrl.includes('?') 
            ? `${baseUrl}&view=basic` 
            : `${baseUrl}?view=basic`;

          // Update active state immediately
          colorPicker.querySelectorAll('a').forEach(a => a.classList.remove('active'));
          anchor.classList.add('active');

          // Check if cached (instant) or need to fetch (show loading)
          if (this.productCache.has(newProductUrl)) {
            // Instant update from cache
            this.renderProduct(this.productCache.get(newProductUrl));
          } else {
            // Show loading state
            this.modalContent.style.opacity = '0.5';
            this.modalContent.style.pointerEvents = 'none';

            this.fetchAndRenderProduct(newProductUrl)
              .finally(() => {
                this.modalContent.style.opacity = '1';
                this.modalContent.style.pointerEvents = 'auto';
              });
          }
        });
      }

      // Remove scripts to prevent duplicate variable declarations
      removeScripts(productElement) {
        const scripts = productElement.querySelectorAll('script');
        scripts.forEach(script => {
          const content = script.textContent?.trim() || '';
          const isJsonData = script.type === 'application/json' || 
                            content.startsWith('{') || 
                            content.startsWith('[');
          
          if (!isJsonData) {
            script.remove();
          }
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector(
          'pickup-availability',
        );
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(
          oldId,
          newId,
        );
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(oldId, newId),
            );
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector(
          '[id^="Slider-Gallery"]',
        );
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) =>
          img.setAttribute('sizes', mediaImageSizes),
        );
      }
    },
  );
}